<template>
  <div class="flex-1 relative">
    <input
      ref="inputRef"
      :value="inputValue"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="onKeydown"
      @paste="onPaste"
      type="text"
      class="w-full px-3 py-2 text-sm border border-secondary-200 dark:border-secondary-700 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent bg-white dark:bg-secondary-900 text-secondary-900 dark:text-white"
      :placeholder="placeholder"
    />

    <!-- Autocomplete Dropdown -->
    <div
      v-if="showDropdown"
      class="absolute left-0 top-full mt-1 w-full bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-xl shadow-xl z-50 max-h-60 overflow-y-auto"
    >
      <div v-if="isSearching" class="px-4 py-3 text-sm text-secondary-400">
        Buscando contactos...
      </div>

      <div v-if="showingSuggestions && !isSearching" class="px-4 py-2 text-xs font-semibold text-secondary-500 uppercase tracking-wide">
        Contactos sugeridos
      </div>

      <div
        v-for="(contact, index) in displayedContacts"
        :key="contact.id + '-' + index"
        @mousedown.prevent="selectContact(contact)"
        :class="[
          'flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors',
          highlightedIndex === index
            ? 'bg-secondary-100 dark:bg-secondary-700'
            : 'hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
        ]"
      >
        <div class="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
          <span class="text-xs font-medium text-primary-700 dark:text-primary-300">
            {{ getInitials(contact.displayName) }}
          </span>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-secondary-900 dark:text-white truncate">
            {{ contact.displayName }}
          </p>
          <p class="text-xs text-secondary-500 dark:text-secondary-400 truncate">
            {{ contact.emailAddresses[0]?.address }}
            <span v-if="contact.company"> · {{ contact.company }}</span>
          </p>
        </div>
      </div>

      <div
        v-if="!isSearching && displayedContacts.length === 0 && inputValue.trim()"
        class="px-4 py-3 text-sm text-secondary-400"
      >
        Sin resultados. Presiona Enter para agregar manualmente.
      </div>
    </div>

    <!-- Recipient Tags -->
    <!-- Una plantilla puede traer diez o veinte direcciones. La lista se limita
         en alto y hace scroll para no empujar el asunto y el editor fuera de la
         vista, y cada ficha muestra el nombre (el correo queda en el tooltip)
         para que quepan varias por línea en vez de una sola. -->
    <!-- El contenedor se dibuja siempre, aunque no haya fichas: un campo vacío
         tiene que poder recibir una ficha que viene de otro campo. Sin arrastre
         no ocupa espacio; mientras viaja una ficha, todos los campos muestran
         su zona de soltado. -->
    <VueDraggableNext
      v-model="recipients"
      tag="div"
      data-testid="recipient-list"
      :group="group"
      :animation="150"
      :set-data="setData"
      ghost-class="opacity-40"
      :class="[
        'flex flex-wrap gap-2 max-h-28 overflow-y-auto',
        recipients.length > 0 || dragging ? 'mt-2' : '',
        dragging ? 'min-h-7 rounded-lg outline-dashed outline-1 outline-secondary-300 dark:outline-secondary-600' : '',
      ]"
      @start="onDragStart"
      @end="onDragEnd"
    >
      <span
        v-for="(recipient, index) in recipients"
        :key="recipient.email"
        :class="['inline-flex max-w-full items-center space-x-1 px-3 py-1 rounded-full text-sm cursor-grab', pillColorClass]"
        :title="formatRecipient(recipient)"
      >
        <span class="min-w-0 truncate">{{ recipient.name || recipient.email }}</span>
        <button
          @click="removeRecipient(index)"
          class="flex-shrink-0 hover:bg-primary-200 dark:hover:bg-primary-800 rounded-full p-0.5"
        >
          <X class="w-3 h-3" />
        </button>
      </span>
    </VueDraggableNext>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, nextTick } from 'vue';
import { VueDraggableNext } from 'vue-draggable-next';
import { X } from 'lucide-vue-next';
import { useContactsSearch } from '@/composables/useContactsSearch';
import { formatEmailAddress } from '@/utils';
import type { EmailAddress } from '@/types';
import type { Contact } from '@/types';

const props = withDefaults(defineProps<{
  modelValue: EmailAddress[];
  inputValue: string;
  placeholder?: string;
  pillColorClass?: string;
  /** Los campos con el mismo grupo se pasan fichas entre sí (Para ↔ CC ↔ CCO) */
  group?: string;
  /** Hay una ficha en el aire en el formulario: el campo muestra su zona de soltado */
  dragging?: boolean;
}>(), {
  placeholder: 'destinatario@ejemplo.com',
  pillColorClass: 'bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300',
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: EmailAddress[]): void;
  (e: 'update:inputValue', value: string): void;
  (e: 'parse'): void;
  (e: 'drag-start'): void;
  (e: 'drag-end'): void;
}>();

// Una dirección no se repite dentro de un campo: si al soltar una ficha ya
// estaba la misma dirección, queda la primera que aparece en la lista.
// También se aplica a lo que llega de afuera (un borrador o una plantilla viejos
// pueden traerla repetida): el campo dibuja y deja arrastrar esta misma lista, así
// las fichas, sus claves y los índices de la librería de arrastre coinciden.
function withoutDuplicates(list: EmailAddress[]): EmailAddress[] {
  const seen = new Set<string>();
  return list.filter((r) => {
    const key = r.email.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

const recipients = computed({
  get: () => withoutDuplicates(props.modelValue),
  set: (value: EmailAddress[]) => emit('update:modelValue', withoutDuplicates(value)),
});

// Sortable pone por defecto el texto de la ficha como dato del arrastre, y al
// soltarla sobre un <input> (el Asunto, el campo de destinatarios) ese texto se
// escribiría allí. Un tipo que no es texto lo evita; Firefox exige que se fije
// algún dato para iniciar el arrastre, por eso no se deja vacío.
function setData(dataTransfer: DataTransfer, dragEl: HTMLElement) {
  dataTransfer.setData('application/x-recipient', dragEl.title);
}

// Si este campo desaparece con un arrastre empezado aquí, el padre no recibiría
// nunca el fin y se quedaría mostrando las zonas de soltado.
let dragStartedHere = false;

function onDragStart() {
  dragStartedHere = true;
  emit('drag-start');
}

function onDragEnd() {
  dragStartedHere = false;
  emit('drag-end');
}

const inputRef = ref<HTMLInputElement | null>(null);
const isFocused = ref(false);
let blurTimer: ReturnType<typeof setTimeout> | null = null;

const {
  results,
  suggestedContacts,
  isSearching,
  highlightedIndex,
  search,
  loadSuggested,
  contactToEmailAddress,
  navigateDown,
  navigateUp,
  resetSearch,
  cleanup,
} = useContactsSearch();

const showingSuggestions = computed(() =>
  isFocused.value && !props.inputValue.trim() && suggestedContacts.value.length > 0 && results.value.length === 0
);

const displayedContacts = computed<Contact[]>(() => {
  if (results.value.length > 0) return results.value;
  if (showingSuggestions.value) return suggestedContacts.value;
  return [];
});

const showDropdown = computed(() =>
  isFocused.value && (displayedContacts.value.length > 0 || isSearching.value || (props.inputValue.trim() && results.value.length === 0 && !isSearching.value))
);

function onInput(event: Event) {
  const value = (event.target as HTMLInputElement).value;
  emit('update:inputValue', value);
  search(value);
}

function onFocus() {
  isFocused.value = true;
  if (!props.inputValue.trim()) {
    loadSuggested();
  }
}

function onBlur() {
  blurTimer = setTimeout(() => {
    isFocused.value = false;
    resetSearch();
    if (props.inputValue.trim()) {
      emit('parse');
    }
  }, 150);
}

/**
 * Pegar una lista de direcciones ("Juan <juan@x.com>; ana@y.com, luis@z.com",
 * una por línea, etc.) crea las fichas de inmediato. Antes solo se parseaba al
 * salir del campo o con Enter, y el usuario veía todo como un texto plano.
 */
function onPaste(event: ClipboardEvent) {
  const text = event.clipboardData?.getData('text') ?? '';
  if (!/[,;\n\r<>@]/.test(text) || !/@/.test(text)) return; // no parece una lista de correos

  event.preventDefault();
  const merged = props.inputValue.trim() ? `${props.inputValue.trim()}; ${text}` : text;
  emit('update:inputValue', merged);
  resetSearch();
  nextTick(() => emit('parse'));
}

function onKeydown(event: KeyboardEvent) {
  const list = displayedContacts.value;

  // Coma o punto y coma tras una dirección → convertirla en ficha de una vez
  if ((event.key === ',' || event.key === ';') && props.inputValue.trim()) {
    event.preventDefault();
    emit('parse');
    return;
  }

  if (event.key === 'ArrowDown' && list.length > 0) {
    event.preventDefault();
    navigateDown(list.length);
    return;
  }

  if (event.key === 'ArrowUp' && list.length > 0) {
    event.preventDefault();
    navigateUp();
    return;
  }

  if (event.key === 'Escape') {
    resetSearch();
    isFocused.value = false;
    inputRef.value?.blur();
    return;
  }

  if (event.key === 'Enter') {
    event.preventDefault();
    if (highlightedIndex.value >= 0 && list.length > 0) {
      selectContact(list[highlightedIndex.value]);
    } else {
      emit('parse');
    }
    return;
  }
}

function selectContact(contact: Contact) {
  if (blurTimer) clearTimeout(blurTimer);
  const emailAddress = contactToEmailAddress(contact);
  emit('update:modelValue', withoutDuplicates([...props.modelValue, emailAddress]));
  emit('update:inputValue', '');
  resetSearch();
  inputRef.value?.focus();
}

function removeRecipient(index: number) {
  const updated = [...recipients.value];
  updated.splice(index, 1);
  emit('update:modelValue', updated);
}

function formatRecipient(recipient: EmailAddress): string {
  return formatEmailAddress(recipient);
}

function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name[0].toUpperCase();
}

onBeforeUnmount(() => {
  cleanup();
  if (blurTimer) clearTimeout(blurTimer);
  if (dragStartedHere) emit('drag-end');
});
</script>
